import clsx from 'clsx';
import React, {
  Suspense, useCallback, useMemo, useState
} from 'react';
import {
  FiArrowLeft, FiEdit2, FiMessageSquare, FiMoreVertical, FiServer, FiTrash2
} from 'react-icons/fi';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Button } from '@/components/button/button';
import { DeleteModal } from '@/components/deleteModal/deleteModal';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { Loader } from '@/components/loader/loader';
import { PropertyItem } from '@/components/propertyItem/propertyItem';
import { Section, Subsection } from '@/components/section/section';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useDeleteModel, useModelInfoQuery } from '@/hooks/queries/useModelsQuery';
import { useProvidersQuery } from '@/hooks/queries/useProvidersQuery';
import { parseParams } from '@/utils/searchParams';

import { ModelModal } from '../modelModal';
import styles from './page.module.scss';

type SearchParams = {
  id: string;
};

function ModelDetailsPage() {
  const navigate = useNavigate();
  const [ searchParams ] = useSearchParams();
  const { id: modelId } = parseParams<SearchParams>( searchParams );
  const [ isEditModalOpen, setIsEditModalOpen ] = useState( false );
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState( false );
  const [ wasDeleted, setWasDeleted ] = useState( false );
  // Fetch model info
  const modelInfoQueryOptions = useMemo(
    () => ( { enabled: !!modelId } ),
    [ modelId ]
  );
  const {
    data: modelInfo,
    isLoading,
    error,
    refetch
  } = useModelInfoQuery( modelId, modelInfoQueryOptions );

  // Fetch providers for display names
  const { data: providers = [], isLoading: isLoadingProviders } = useProvidersQuery( { enabled: true } );

  // Delete mutation
  const deleteModelMutation = useDeleteModel();

  const handleBack = () => {
    navigate( '/models' );
  };

  const handleEditClick = useCallback( () => {
    setIsEditModalOpen( true );
  }, [] );

  const handleCloseEditModal = useCallback( () => {
    setIsEditModalOpen( false );
  }, [] );

  const handleEditSuccess = useCallback( () => {
    toast.success( 'Model updated successfully' );
    refetch();
  }, [ refetch ] );

  const handleDeleteClick = useCallback( () => {
    setWasDeleted( false );
    setIsDeleteModalOpen( true );
  }, [] );

  const handleCloseDeleteModal = useCallback( () => {
    setIsDeleteModalOpen( false );
    if ( wasDeleted ) {
      // Pass state so the Models list page can show the success toast after
      // this page (and its <ToastContainer />) unmounts on navigation.
      navigate( '/models', { state: { deletedToast: 'Model deleted successfully' } } );
    }
  }, [ wasDeleted, navigate ] );

  const handleConfirmDelete = useCallback( async() => {
    if ( !modelId ) return;

    await deleteModelMutation.mutateAsync( modelId );
    setWasDeleted( true );
  }, [ modelId, deleteModelMutation ] );

  if ( isLoading ) {
    return (
      <div className='page'>
        <Loader />
      </div>
    );
  }

  if ( error ) {
    return (
      <div className='page'>
        <div className={ clsx( 'layout__row', 'pad' ) }>
          <Text as="p" size="md">
            Error loading model: {error instanceof Error ? error.message : 'Unknown error'}
          </Text>
          <Button
            variant="secondary"
            size="md"
            icon={ <Icon icon={ FiArrowLeft } /> }
            onClick={ handleBack }
          >
            Back to Models
          </Button>
        </div>
      </div>
    );
  }

  if ( !modelInfo || !modelInfo.data || modelInfo.data.length === 0 ) {
    return (
      <div className='page'>
        <div className={ clsx( 'layout__row', 'pad' ) }>
          <Text as="p" size="md">
            Model not found
          </Text>
          <Button
            variant="secondary"
            size="md"
            icon={ <Icon icon={ FiArrowLeft } /> }
            onClick={ handleBack }
          >
            Back to Models
          </Button>
        </div>
      </div>
    );
  }

  const modelData = modelInfo.data[0];
  const providerFromModel = modelData.litellm_params.model.split( '/' )[0];
  const matchingProvider = providers?.find(
    p => p.litellm_provider.toLowerCase() === providerFromModel.toLowerCase()
  );
  const modelName = modelData.model_name;
  const params = new URLSearchParams( { mn: modelName } );
  const playgroundUrl = `/models/playground/?${ params.toString() }`;

  return (
    <div className='page'>
      <ToastContainer />

      {/* Back Button */}
      <div className={ clsx( 'layout__row', 'pad', styles.navigation ) }>
        <Button
          variant="secondary"
          size="md"
          icon={ <Icon icon={ FiArrowLeft } /> }
          onClick={ handleBack }
        >
          Back to Models
        </Button>
      </div>

      {/* Header with icon, title, description, and actions */}
      <div className={ styles.header }>
        <div className={ styles.typeIcon }>
          <FiServer />
        </div>
        <div className={ styles.titleSection }>
          <Heading as="h1" size="lg">{modelData.model_name}</Heading>
          {modelData.model_info?.description && (
            <p className={ styles.description }>{modelData.model_info.description}</p>
          )}
        </div>
        <div className={ styles.actions }>
          <Link
            to={ playgroundUrl }
            className={ styles.buttonLink }
          >
            <Button
              variant='primary'
              icon={ <Icon icon={ FiMessageSquare } /> }
            >
              Playground
            </Button>
          </Link>
          <Menu
            icon={ <Icon icon={ FiMoreVertical } /> }
            variant='menu'
          >
            <MenuItem
              value="Edit model"
              onClick={ handleEditClick }
              leadingIcon={ <Icon icon={ FiEdit2 } size="sm" /> }
            />
            <MenuItem
              value="Delete model"
              onClick={ handleDeleteClick }
              leadingIcon={ <Icon icon={ FiTrash2 } size="sm" /> }
            />
          </Menu>
        </div>
      </div>

      {/* Properties Section */}
      <div className={ clsx( 'pad' ) }>
        <Section title="Properties">
          <Subsection>
            {/* First Row - Model Name and Provider Model */}
            <div className={ styles.propertiesRowTwo }>
              <PropertyItem
                label="Model Name"
                value={ modelData.model_name }
                variant="large"
                helperText="Model name is persistent"
              />
              <PropertyItem
                label="Provider Model"
                value={ modelData.litellm_params.model }
                variant="large"
                helperText="Provider model may change"
              />
            </div>

            {/* Second Row - Provider, Model UUID */}
            <div className={ styles.propertiesRowTwo }>
              <PropertyItem
                label="Provider"
                value={ matchingProvider?.provider_display_name || providerFromModel }
              />
              <PropertyItem
                label="Model UUID"
                value={ modelData.model_info?.id || modelId || '-' }
              />
            </div>
          </Subsection>
        </Section>
      </div>

      {/* Edit Modal */}
      <ModelModal
        mode="edit"
        isOpen={ isEditModalOpen }
        onClose={ handleCloseEditModal }
        onSuccess={ handleEditSuccess }
        modelId={ modelId }
        providers={ providers }
        isLoadingProviders={ isLoadingProviders }
      />

      {/* Delete Modal */}
      { isDeleteModalOpen && (
        <DeleteModal
          isOpen={ true }
          onClose={ handleCloseDeleteModal }
          onDelete={ handleConfirmDelete }
          entityName={ modelData.model_name }
          entityType="model"
          body={ <Text as="p" size="md">This model may be used by Low-Code Agents, Full-Code Agents, or evaluations. Deleting it could cause those resources to fail.<br /><br />Please verify and update any affected configurations before proceeding.</Text> }
        />
      ) }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <ModelDetailsPage />
    </Suspense>
  );
}
